import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Reply } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { aiApi, roleAI } from "./aiApi";
import AIMarkdown from "./AIMarkdown";
import AIActionCard from "./AIActionCard";
import { setMickeyMood, celebrateMickey } from "./mickeyMood";

export const INBOX_REPLY_ROLES = ["superadmin", "branch_manager", "admin"];

/** Balas peringatan dengan arahan kepada Mimi Junior. Tindakan dipaparkan sebagai kad yang perlu disahkan. */
export default function AIInboxReply({ notification, user }) {
  const ai = roleAI(user.role);
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    aiApi.inboxThread(notification.id).then((d) => setMessages(d.messages || [])).catch(() => setMessages([]));
  }, [notification.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [messages, sending]);

  const send = async () => {
    const m = text.trim();
    if (!m || sending) return;
    setSending(true);
    setMickeyMood("thinking");
    const temp = { id: `tmp-${Date.now()}`, role: "user", content: m };
    setMessages((x) => [...(x || []), temp]);
    setText("");
    try {
      const d = await aiApi.inboxReply(notification.id, m);
      setMessages((x) => [...(x || []).filter((y) => y.id !== temp.id), d.user_message, d.reply]);
      if ((d.reply?.actions || []).length) toast.info("Sila semak & sahkan tindakan yang dicadangkan Mimi Junior");
      celebrateMickey();
    } catch (e) {
      setMessages((x) => (x || []).filter((y) => y.id !== temp.id));
      setText(m);
      setMickeyMood("idle");
      toast.error(e.response?.data?.detail || "Mimi Junior gagal menjawab");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/70 p-2" data-testid="ai-inbox-reply">
      <div className="flex items-center gap-1 text-[11px] font-bold text-slate-500 mb-1.5">
        <Reply className="h-3.5 w-3.5" /> Balas & beri arahan kepada {ai.name}
      </div>
      {messages === null ? (
        <div className="grid place-items-center h-10"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
      ) : (
        <div className="space-y-2 max-h-[360px] overflow-y-auto pr-0.5" data-testid="ai-inbox-thread">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "user" ? (
                <div className={`max-w-[88%] rounded-2xl rounded-br-md bg-gradient-to-br ${ai.grad} text-white px-3 py-1.5 text-[13px] whitespace-pre-wrap shadow`}>{m.content}</div>
              ) : (
                <div className="w-full rounded-2xl rounded-bl-md bg-white border border-slate-100 px-3 py-2 shadow-sm" data-testid="ai-inbox-thread-reply">
                  <AIMarkdown>{m.content}</AIMarkdown>
                  {(m.actions || []).map((a) => (
                    <AIActionCard key={a.id} action={a} user={user} onDone={(note) => note && setMessages((x) => [...(x || []), note])} />
                  ))}
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-[12px] text-slate-500 px-1" data-testid="ai-inbox-thinking">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {ai.name} sedang memproses arahan...
            </div>
          )}
          <div ref={endRef} />
        </div>
      )}
      <div className="mt-2 flex items-end gap-1.5">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Contoh: Luluskan pendaftaran yang menunggu / Ingatkan guru lengkapkan log harian"
          rows={2}
          className="min-h-[44px] text-[13px] bg-white resize-none"
          disabled={sending}
          data-testid="ai-inbox-reply-input"
        />
        <Button size="icon" onClick={send} disabled={sending || !text.trim()} className={`h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br ${ai.grad} text-white`} aria-label="Hantar arahan" data-testid="ai-inbox-reply-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      <div className="text-[10.5px] text-slate-400 mt-1">Sebarang tindakan akan dipaparkan untuk anda semak & tekan <b>Sahkan</b> dahulu sebelum dilaksanakan.</div>
    </div>
  );
}
